/**
 * NIDO chat assistant. Sends the conversation to the nido-chat worker, which answers with Claude.
 * When the assistant can't help (or the customer asks for a person) it shows a contact form;
 * the worker emails the contact details and the chat to the shop.
 */
(() => {
  if (customElements.get('nido-chat')) return;

  const STORAGE_KEY = 'nido-chat-v1';
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  // the widget's own words, in the page language (snippets/nido-chat.liquid, locales nido.chat)
  const T = JSON.parse(document.querySelector('[data-chat-strings]')?.textContent || '{"links":{}}');

  class NidoChat extends HTMLElement {
    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || '').replace(/\/+$/, '');
      this.sitekey = this.dataset.sitekey || '';
      this.panel = this.querySelector('.nido-chat__panel');
      this.launcher = this.querySelector('[data-chat-toggle]');
      this.log = this.querySelector('[data-chat-log]');
      this.chips = this.querySelector('[data-chat-chips]');
      this.form = this.querySelector('[data-chat-form]');
      this.input = this.querySelector('[data-chat-input]');
      this.sendButton = this.querySelector('[data-chat-send]');
      this.leadTemplate = this.querySelector('[data-lead-template]');

      this.state = { messages: [], session: null, sessionExp: 0, leadOpen: false, leadSent: false, open: false };
      this.restore();

      this.launcher.addEventListener('click', () => this.toggle());
      this.querySelector('[data-chat-close]').addEventListener('click', () => this.toggle(false));
      this.addEventListener('click', (event) => {
        if (event.target === this) this.toggle(false); // backdrop on phones
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.state.open) this.toggle(false);
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.send(this.input.value);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
          event.preventDefault();
          this.send(this.input.value);
        }
      });
      this.input.addEventListener('input', () => this.autosize());
      this.chips.addEventListener('click', (event) => {
        const chip = event.target.closest('button');
        if (!chip) return;
        if (chip.hasAttribute('data-handoff')) this.askForHuman();
        else this.send(chip.dataset.prompt);
      });

      if (this.state.open) this.toggle(true, { focus: false });
    }

    // --- Open / close

    toggle(force, { focus = true } = {}) {
      const open = force ?? !this.state.open;
      this.state.open = open;
      this.classList.toggle('is-open', open);
      this.panel.hidden = !open;
      this.launcher.setAttribute('aria-expanded', String(open));
      this.launcher.setAttribute('aria-label', open ? T.close : T.open);
      this.save();
      if (open) {
        this.prepareTurnstile();
        this.scrollToEnd();
        if (focus) this.input.focus({ preventScroll: true });
      } else if (focus) {
        this.launcher.focus({ preventScroll: true });
      }
    }

    // --- Conversation

    async send(text) {
      text = (text || '').trim();
      if (!text || this.busy) return;
      this.input.value = '';
      this.autosize();
      this.chips.hidden = true;
      this.addMessage('user', text);

      this.setBusy(true);
      const typing = this.showTyping();
      try {
        const data = await this.post('/chat', { messages: this.state.messages });
        typing.remove();
        this.addMessage('assistant', data.reply || T.errorReply);
        if (data.handoff) this.showLeadForm();
      } catch (error) {
        console.error('[nido-chat]', error);
        typing.remove();
        this.addMessage('assistant', T.errorReply, { save: false });
        this.showLeadForm();
      } finally {
        this.setBusy(false);
      }
    }

    askForHuman() {
      this.chips.hidden = true;
      this.addMessage('user', T.human);
      this.addMessage('assistant', T.humanReply);
      this.showLeadForm();
    }

    addMessage(role, content, { save = true } = {}) {
      if (save) {
        this.state.messages.push({ role, content });
        this.save();
      }
      this.renderMessage(role, content);
      this.scrollToEnd();
    }

    renderMessage(role, content) {
      const bubble = document.createElement('div');
      bubble.className = `nido-chat__msg nido-chat__msg--${role}`;
      if (role === 'assistant') bubble.innerHTML = formatReply(content);
      else bubble.textContent = content;
      const lead = this.log.querySelector('.nido-chat__lead');
      this.log.insertBefore(bubble, lead || null);
    }

    showTyping() {
      const typing = document.createElement('div');
      typing.className = 'nido-chat__msg nido-chat__msg--assistant nido-chat__typing';
      typing.setAttribute('aria-label', T.typing);
      typing.innerHTML = '<span></span><span></span><span></span>';
      this.log.appendChild(typing);
      this.scrollToEnd();
      return typing;
    }

    // --- Contact form

    showLeadForm({ save = true } = {}) {
      if (this.state.leadSent || this.log.querySelector('.nido-chat__lead')) return;
      this.state.leadOpen = true;
      if (save) this.save();
      const form = this.leadTemplate.content.firstElementChild.cloneNode(true);
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitLead(form);
      });
      this.log.appendChild(form);
      this.scrollToEnd();
      if (save && this.state.open) form.querySelector('input[name="contact"]').focus({ preventScroll: true });
    }

    async submitLead(form) {
      const error = form.querySelector('.nido-chat__lead-error');
      const button = form.querySelector('button[type="submit"]');
      const name = form.elements.name.value.trim();
      const contact = form.elements.contact.value.trim();
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact);
      const isPhone = /^\+?[\d\s()./-]{6,20}$/.test(contact);
      if (!isEmail && !isPhone) {
        error.textContent = T.invalidContact;
        error.hidden = false;
        form.elements.contact.focus();
        return;
      }
      error.hidden = true;
      button.disabled = true;
      try {
        await this.post('/lead', {
          name,
          contact,
          transcript: this.state.messages,
          page: window.location.href,
        });
        form.remove();
        this.state.leadSent = true;
        this.state.leadOpen = false;
        this.addMessage('assistant', T.thanksReply);
      } catch (err) {
        console.error('[nido-chat]', err);
        error.textContent =
          T.leadFailed;
        error.hidden = false;
        button.disabled = false;
      }
    }

    // --- Network

    async post(path, payload, retry = true) {
      const session = await this.ensureSession();
      const res = await fetch(`${this.endpoint}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...payload, session }),
      });
      if (res.status === 401 && retry) {
        this.state.session = null;
        return this.post(path, payload, false);
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }

    async ensureSession() {
      if (this.state.session && this.state.sessionExp > Date.now() / 1000 + 60) return this.state.session;
      // The bot check occasionally fails on the first try (slow network, expired token); try once more.
      try {
        return await this.createSession();
      } catch (error) {
        console.warn('[nido-chat] retrying session', error);
        return this.createSession();
      }
    }

    async createSession() {
      const turnstileToken = this.sitekey ? await this.getTurnstileToken() : '';
      const res = await fetch(`${this.endpoint}/session`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ turnstileToken }),
      });
      if (!res.ok) throw new Error(`session HTTP ${res.status}`);
      const data = await res.json();
      this.state.session = data.session;
      this.state.sessionExp = data.expiresAt;
      this.save();
      return data.session;
    }

    // Turnstile runs invisibly and only asks for a click if Cloudflare is unsure.
    prepareTurnstile() {
      if (!this.sitekey || this.turnstileReady) return;
      this.turnstileReady = new Promise((resolve, reject) => {
        if (window.turnstile) return resolve(window.turnstile);
        const script = document.createElement('script');
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.onload = () => resolve(window.turnstile);
        script.onerror = () => {
          this.turnstileReady = null;
          reject(new Error('turnstile script'));
        };
        document.head.appendChild(script);
      });
    }

    async getTurnstileToken() {
      this.prepareTurnstile();
      const turnstile = await this.turnstileReady;
      const container = this.querySelector('[data-chat-turnstile]');
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('turnstile timeout')), 30000);
        const done = (fn) => (value) => {
          clearTimeout(timer);
          fn(value);
        };
        if (this.widgetId !== undefined) turnstile.remove(this.widgetId);
        this.widgetId = turnstile.render(container, {
          sitekey: this.sitekey,
          appearance: 'interaction-only',
          language: document.documentElement.lang || 'auto',
          callback: done(resolve),
          'error-callback': (code) => done(reject)(new Error(`turnstile ${code}`)),
          'expired-callback': () => done(reject)(new Error('turnstile expired')),
        });
      });
    }

    // --- Helpers

    setBusy(busy) {
      this.busy = busy;
      this.sendButton.disabled = busy;
    }

    autosize() {
      this.input.style.height = 'auto';
      this.input.style.height = `${Math.min(this.input.scrollHeight, 120)}px`;
    }

    scrollToEnd() {
      requestAnimationFrame(() => {
        this.log.scrollTop = this.log.scrollHeight;
      });
    }

    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        if (saved && Array.isArray(saved.messages)) Object.assign(this.state, saved);
      } catch {
        // Storage unavailable: start fresh.
      }
      if (this.state.messages.length) this.chips.hidden = true;
      for (const message of this.state.messages) this.renderMessage(message.role, message.content);
      if (this.state.leadOpen && !this.state.leadSent) this.showLeadForm({ save: false });
    }

    save() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch {
        // Ignore: the chat still works for this page view.
      }
    }
  }

  // Escapes the reply, then allows **bold**, line breaks and links to the store only.
  function formatReply(text) {
    const ctas = [];
    const html = escapeHtml(text)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, (match, label, url) => link(url, label) || label)
      // Bare page addresses become a button under the answer instead of a raw URL in the text.
      .replace(/(^|[\s(])(https?:\/\/[^\s<)]+[^\s<).,!?;:])/g, (match, lead, url) => {
        const cta = link(url, pageLabel(url), 'nido-chat__cta');
        if (cta && !ctas.includes(cta)) ctas.push(cta);
        return lead;
      })
      .replace(/(^|[\s(])([\w.+-]+@[\w-]+\.[\w.]+[\w])/g, '$1<a href="mailto:$2">$2</a>')
      // Tidy what is left after a URL was lifted out ("Вижте тук: ." becomes "Вижте тук.").
      .replace(/[ \t]*:[ \t]*(?=[.,;!?]|\n|$)/g, '')
      .replace(/[ \t]+([.,;!?])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .trim()
      .replace(/\n/g, '<br>');
    return ctas.length ? `${html}<br>${ctas.join(' ')}` : html;
  }

  const PAGE_LABELS = [
    [/^\/pages\/baby-personalization/, 'personalize'],
    [/^\/pages\/washing-instructions/, 'washing'],
    [/^\/pages\/contact/, 'contact'],
    [/^\/policies\/refund/, 'refund'],
    [/^\/policies\/shipping/, 'shipping'],
    [/^\/policies\//, 'policies'],
    [/^\/products\//, 'product'],
    [/^\/collections\//, 'collection'],
    [/^\/?$/, 'home'],
  ];

  function pageLabel(url) {
    try {
      const path = new URL(url.replace(/&amp;/g, '&')).pathname;
      const match = PAGE_LABELS.find(([pattern]) => pattern.test(path));
      return `${T.links[match ? match[1] : 'page']}\u00a0→`;
    } catch {
      return `${T.links.page}\u00a0→`;
    }
  }

  function escapeHtml(text) {
    return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  function link(url, label, className) {
    try {
      const parsed = new URL(url.replace(/&amp;/g, '&'));
      if (!/(^|\.)nidoknit\.com$/.test(parsed.hostname) && parsed.hostname !== window.location.hostname) return '';
      // the assistant links to the Bulgarian pages; keep the visitor in their language ("/en/…")
      const root = window.Shopify?.routes?.root || '/';
      if (root !== '/' && !parsed.pathname.startsWith(root)) parsed.pathname = root + parsed.pathname.replace(/^\//, '');
      const cls = className ? ` class="${className}"` : '';
      return `<a${cls} href="${parsed.href.replace(/"/g, '&quot;')}">${label}</a>`;
    } catch {
      return '';
    }
  }

  customElements.define('nido-chat', NidoChat);
})();
